import React, { useEffect, useMemo, useRef, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson, appFetchJson } from "../../../utils/http";
/* eslint-env es2020 */

/**
 * Cambiar estado de tarjetas de un cliente
 * Props:
 *  - cliente: objeto del cliente (debe tener al menos { id, codigo, nombre })
 *  - onClose: fn
 *  - onSaved: fn(updatedSummary)  -> { total, seleccionadas, estadoNuevoId, estadoNuevoNombre, actualizadas, rechazadas }
 */
export default function CambiarEstadoTarjetas({ cliente, onClose, onSaved }) {
  const ESTADO_CANCELADA_ID = "19";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [confirmCancel, setConfirmCancel] = useState(false);

  const [tarjetas, setTarjetas] = useState([]); // lista completa
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set()); // IDs string
  const [openPicker, setOpenPicker] = useState(false);

const pickerRef = useRef(null);
const buttonRef = useRef(null);

// cerrar picker si clickeás afuera
useEffect(() => {
  function handleClickOutside(e) {
    if (
      openPicker &&
      pickerRef.current &&
      !pickerRef.current.contains(e.target) &&
      buttonRef.current &&
      !buttonRef.current.contains(e.target)
    ) {
      setOpenPicker(false);
    }
  }

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, [openPicker]);

const estadoRef = useRef(null);
const estadoBtnRef = useRef(null);

// Dropdown custom para estado (Solución B)
const [openEstado, setOpenEstado] = useState(false);
// cerrar dropdown de estado si clickeás afuera
useEffect(() => {
  function handleClickOutside(e) {
    if (
      openEstado &&
      estadoRef.current &&
      !estadoRef.current.contains(e.target) &&
      estadoBtnRef.current &&
      !estadoBtnRef.current.contains(e.target)
    ) {
      setOpenEstado(false);
    }
  }

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, [openEstado]);


  const [estados, setEstados] = useState([]); // [{id, nombre}]
  const [estadoNuevo, setEstadoNuevo] = useState(""); // id (string)
// Nombre visible del estado seleccionado
const estadoNuevoNombre = useMemo(() => {
  if (!estadoNuevo) return "";
  const found = estados.find((e) => String(e.id) === String(estadoNuevo));
  return found?.nombre || "";
}, [estadoNuevo, estados]);

  const toId = (v) => (v != null ? String(v) : "");

  // cliente activo: si necesitás validar por estado real, cambiá acá
  const clienteActivo = true;

  // ====== ENDPOINTS (derivados del /clientes/list que ya tenés) ======
  const CLIENTES_BASE = (apiRoutes?.clientes?.list || "").replace(
    /\/list(?:\?.*)?$/,
    "",
  );
  const TARJETAS_ESTADOS_URL = (clienteId) =>
    `${CLIENTES_BASE}/${encodeURIComponent(clienteId)}/tarjetas/estados`;
  const CAMBIO_ESTADO_URL = (clienteId) =>
    `${CLIENTES_BASE}/${encodeURIComponent(clienteId)}/tarjetas/estado`;

  // 👇 Evitar doble GET en desarrollo (StrictMode): saltamos la PRIMERA ejecución
  const devRunRef = useRef(false);

  // ====== CARGA INICIAL DESDE EL NUEVO ENDPOINT ======
  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!cliente?.id) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const url = TARJETAS_ESTADOS_URL(cliente.id);
        // const resp = await fetch(url, {
        //                       headers: authHeaders()
        //                     });
        // if (!resp.ok) {
        //   const t = await resp.text();
        //   throw new Error(`GET ${url} -> ${resp.status} ${resp.statusText}. Body: ${t}`);
        // }
        // const data = await resp.json();
        const data = await apiFetchJson(url); // ✅ wrapper (401/403 + JSON)
        if (!alive) return;

        // 1) Estados de tarjeta (vienen ya filtrados por el backend)
        const estadosList = Array.isArray(data?.estadosTarjeta)
          ? data.estadosTarjeta
          : [];
        setEstados(
          estadosList
            .map((e) => ({
              id: toId(e.id ?? e.esta_sq_cod ?? e.codigo),
              nombre: e.nombre ?? e.esta_nm_estado ?? "",
            }))
            .filter((e) => e.id && e.nombre),
        );

        // 2) Tarjetas del cliente (ya sin canceladas)
        const tList = Array.isArray(data?.tarjetas) ? data.tarjetas : [];
        const norm = tList
          .map((t) => ({
            id: toId(t.id),
            numero: toId(t.numero ?? t.id ?? ""),
            estado: t.estadoNombre ?? "",
            vehiculo: t.marca ?? t.marca ?? "",
            patente: t.patente ?? t.patente ?? "",
            descricaoDetalles: t.descricaoDetalles ?? t.descricaoDetalles ?? "",
            cuenta: t.cuenta ?? t.codigoCuenta ?? "",
            raw: t,
          }))
          .filter((t) => t.id);
        setTarjetas(norm);

        // por defecto: todas preseleccionadas
        setSelectedIds(new Set(norm.map((x) => toId(x.id))));
      } catch (err) {
        console.error("[CambiarEstadoTarjetas] Error cargando datos:", err);
        alert("No se pudieron cargar tarjetas/estados. Revisá la consola.");
      } finally {
        if (alive) setLoading(false);
      }
    };

    if (process.env.NODE_ENV !== "production") {
      if (!devRunRef.current) {
        devRunRef.current = true; // primera pasada en dev → no ejecutar aún
        return;
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [cliente?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ====== Filtro de búsqueda en el picker ======
  const filtradas = useMemo(() => {
    if (!search) return tarjetas;
    const q = search.toLowerCase();
    return tarjetas.filter((t) =>
      [t.id, t.numero, t.estado, t.vehiculo,t.patente,t.descricaoDetalles, t.cuenta]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [tarjetas, search]);

  const total = tarjetas.length;
  const seleccionadas = selectedIds.size;

  // ====== Selección (NO TOCADA) ======
  const toggleOne = (id) => {
    const key = toId(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const marcarTodas = () =>
    setSelectedIds(new Set(tarjetas.map((t) => toId(t.id))));
  const desmarcarTodas = () => setSelectedIds(new Set());

  const canSave = seleccionadas > 0 && estadoNuevo !== "";

  const descripcionTarjeta = (t) => {
    const partes = [
      t.numero || t.id,
      t.cuenta && `- ${t.cuenta}`,
      t.estado && `- ${t.estado}`,
      t.vehiculo && `- ${t.vehiculo}`,
      t.patente && `- ${t.patente}`,
      t.descricaoDetalles && `- ${t.descricaoDetalles}`,
    ].filter(Boolean);
    return partes.join(" ");
  };

  // ====== Guardar: integra PATCH /clientes/{clienteId}/tarjetas/estado ======
  const handleSave = async (e) => {
  e?.preventDefault?.();
  if (!canSave) return;

  // ⚠️ Solo si se seleccionó "Tarjeta Cancelada"
  if (estadoNuevo === ESTADO_CANCELADA_ID) {
    setConfirmCancel(true);
    return;
  }

  // flujo normal
  await doSave();
};

const doSave = async () => {
  setSaving(true);
  try {
    const estadoIdNum = Number(estadoNuevo);
    const idsNum = Array.from(selectedIds).map((x) => Number(x));

    const url = CAMBIO_ESTADO_URL(cliente.id);

    const result = await appFetchJson(url, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        estadoId: estadoIdNum,
        tarjetaIds: idsNum,
      }),
    });

    const rechazadasSet = new Set((result.rechazadas || []).map(String));
    const nuevoNombre =
      result.estadoNombre ||
      estados.find((e) => e.id === String(estadoIdNum))?.nombre ||
      "";

    setTarjetas((prev) =>
      prev.map((t) =>
        selectedIds.has(String(t.id)) && !rechazadasSet.has(String(t.id))
          ? { ...t, estado: nuevoNombre }
          : t,
      ),
    );

    onSaved?.({
      total,
      seleccionadas,
      estadoNuevoId: String(estadoIdNum),
      estadoNuevoNombre: nuevoNombre,
      actualizadas: result.actualizadas ?? 0,
      rechazadas: result.rechazadas ?? [],
    });

    onClose?.();
  } catch (err) {
    console.error(err);
    alert("No se pudo cambiar el estado de las tarjetas.");
  } finally {
    setSaving(false);
    setConfirmCancel(false);
  }
};

// <<< CONFIRMACIÓN DE CANCELACIÓN (PEGAR JUSTO ANTES DEL return)
if (confirmCancel) {
  return (
    <ConfirmModal
      message={`¿Está seguro que desea cancelar ${selectedIds.size} tarjeta/s?`}
      onAccept={async () => {
        await doSave();   // ejecuta el PATCH real
      }}
      onCancel={() => setConfirmCancel(false)}
    />
  );
}

  return (
    <Modal title="Cambiar Estado de Tarjetas" onClose={onClose}>
      {loading ? (
        <div className="py-10 text-center">Cargando...</div>
      ) : !clienteActivo ? (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
          El cliente no está <b>ACTIVO</b>. No es posible cambiar estados de
          tarjetas.
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-5">
          {/* █ NUEVA FILA: Cliente sobre el que se trabaja */}
          <div className="border rounded bg-gray-50">
            <div className="px-4 py-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="sm:w-40 text-gray-700 font-medium">
                  Cliente:
                </div>
                <div className="flex-1">
                  <div className="py-2 text-gray-800">
                    {cliente ? (
                      <>
                        <b>{cliente.codigo ?? cliente.id}</b>
                        {cliente.nombre ? ` — ${cliente.nombre}` : ""}
                      </>
                    ) : (
                      <span className="text-gray-500">Sin cliente</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* █ FIN agregado */}

          <div className="border rounded bg-gray-50">
            <div className="px-4 py-2 border-b text-gray-700">
              Cambie aquí el estado de las tarjetas del cliente seleccionado
            </div>

            {/* Tarjeta (multi-select con dropdown estilo "picker") */}
            <div className="p-4 space-y-3">
              <div className="mb-3">
                <div className="text-gray-700 mb-1">Tarjeta:</div>

                <div ref={buttonRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setOpenPicker((o) => !o)}
                    className="w-full border rounded px-3 py-2 text-left bg-white hover:bg-gray-50"
                  >
                    {seleccionadas} {seleccionadas === 1 ? "seleccionada" : "seleccionadas"}
                  </button>

                  {openPicker && (
                    <div
                      ref={pickerRef}
                      className="mt-2 w-full border rounded bg-white shadow-lg"
                      style={{ position: "relative" }}
                    >
                      {/* barra superior: filtro + acciones */}
                      <div className="p-2 border-b bg-gray-50 flex flex-wrap items-center gap-2">
                        <input
                          placeholder="Filtrar registro"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="flex-1 border rounded px-2 py-1"
                        />
                        <span className="text-sm text-gray-600">Opciones:</span>
                        <button
                          type="button"
                          onClick={marcarTodas}
                          className="text-green-700 text-sm hover:underline"
                        >
                          ✓ Marcar todas
                        </button>
                        <button
                          type="button"
                          onClick={desmarcarTodas}
                          className="text-green-700 text-sm hover:underline"
                        >
                          ✗ Desmarcar todas
                        </button>
                      </div>

                      {/* lista scrolleable */}
                      <div className="max-h-72 overflow-auto">
                        {filtradas.map((t) => (
                          <label
                            key={toId(t.id)}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.has(toId(t.id))}
                              onChange={() => toggleOne(t.id)}
                            />
                            <span className="text-sm">
                              {descripcionTarjeta(t)}
                            </span>
                          </label>
                        ))}
                        {filtradas.length === 0 && (
                          <div className="px-3 py-4 text-sm text-gray-500">
                            Sin resultados
                          </div>
                        )}
                      </div>

                      {/* cerrar */}
                      <div className="p-2 border-t text-right">
                        <button
                          type="button"
                          onClick={() => setOpenPicker(false)}
                          className="text-green-700 hover:text-green-800"
                        >
                          Cerrar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Estado */}
              <div className="relative">
                <div className="text-gray-700 mb-1">Estado:</div>
                  <button
                    ref={estadoBtnRef}
                    type="button"
                    onClick={() => setOpenEstado((o) => !o)}
                    className="w-full border rounded px-3 py-2 bg-white text-left"
                  >
                    {estadoNuevoNombre || "--- Seleccione ---"}
                  </button>
                  {openEstado && (
                    <div
                      ref={estadoRef}
                      className="mt-2 w-full border rounded bg-white shadow-lg p-1 max-h-60 overflow-auto"
                    >
                      {estados.map(e => (
                        <div
                          key={e.id}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setEstadoNuevo(e.id);
                            setOpenEstado(false);
                          }}
                        >
                          {e.nombre}
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="submit"
              disabled={!canSave || saving}
              className="btn-fil disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? "Guardando..." : "Guardar"}
            </button>

            <button type="button" onClick={onClose} className="btn-fil">
              Cancelar
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/* ---------- UI helpers ---------- */
function Modal({ title, children, onClose }) {
  return (
    <div className="modal-backdrop">
      <div className="modal-panel-md">
        <div className="modal-header flex justify-between items-center">
          <h2 className="section-title">{title}</h2>
          {/* <button onClick={onClose} className="modal-close-x">✕</button> */}
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
// <<< COMPONENTE CONFIRM MODAL
function ConfirmModal({ message, onAccept, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="modal-panel-sm bg-white rounded shadow-lg p-4 max-w-sm w-full">
        <div className="modal-header">
          <h2 className="section-title">Confirmar acción</h2>
        </div>

        <div className="modal-body space-y-4 mt-2">
          <p className="text-gray-800">{message}</p>

          <div className="flex justify-end gap-2">
            <button
              onClick={onAccept}
              className="btn-fil bg-green-600 text-white hover:bg-green-700"
            >
              Aceptar
            </button>

            <button
              onClick={onCancel}
              className="btn-fil bg-gray-300 hover:bg-gray-400"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
